import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { FooterProps } from 'src/components/Footer'
import { KalioFirstLevelItem } from 'src/models/Menu/Menu'
import { Season } from 'src/types/fam-graphql'

import type { RootState } from '../store'

import { actionTypes } from './types'

type AppState = {
  isInit: boolean
  isLoading: boolean
  menu: KalioFirstLevelItem[] | null
  footerData: {
    menu: FooterProps['menu']
    legals: FooterProps['legals']
  } | null
  currentSeason: Season | null
  isOffline: boolean
  feedback: string | null
  afterLoginJobs: PayloadAction[]
}

//
// Initial state
//

const initialState: AppState = {
  isInit: false,
  isLoading: false,
  menu: null,
  footerData: null,
  currentSeason: null,
  isOffline: false,
  feedback: null,
  afterLoginJobs: [],
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    init: () => undefined,
    mount: () => undefined,
    mountOffline: () => undefined,
    setIsInit: (state, action: actionTypes.setIsInit) => {
      const { isInit } = action.payload
      state.isInit = isInit
    },
    setIsLoading: (state, action: actionTypes.setIsLoading) => {
      state.isLoading = action.payload
    },
    setMenu: (state, action: actionTypes.setMenu) => {
      const { menu } = action.payload
      state.menu = menu
    },
    setFooterData: (state, action: actionTypes.setFooterData) => {
      const { menu, legals } = action.payload
      state.footerData = { menu, legals }
    },
    setCurrentSeason: (state, action: actionTypes.setCurrentSeason) => {
      const { currentSeason } = action.payload
      state.currentSeason = currentSeason
    },
    setIsOffline: (state, action: actionTypes.setIsOffline) => {
      const { isOffline } = action.payload
      state.isOffline = isOffline
    },
    setFeedback: (state, action: actionTypes.setFeedback) => {
      state.feedback = action.payload
    },
    setAfterLoginJobs: (state, action: actionTypes.setAfterLoginJobs) => {
      state.afterLoginJobs = action.payload
    },
    addToAfterLoginJobs: (state, action: actionTypes.addToAfterLoginJobs) => {
      state.afterLoginJobs.push(action.payload)
      if (window && window.localStorage) {
        window.localStorage.setItem(
          'afterLoginJobs',
          JSON.stringify(state.afterLoginJobs)
        )
      }
    },
    clearAfterLoginJobs: (state) => {
      state.afterLoginJobs = []
      if (window && window.localStorage) {
        window.localStorage.removeItem('afterLoginJobs')
      }
    },
  },
  extraReducers: {
    [HYDRATE]: (_state, action) => {
      return action.payload.app
    },
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state.app
const isInit = (state: RootState) => root(state).isInit
const isLoading = (state: RootState) => root(state).isLoading
const menu = (state: RootState) => root(state).menu
const footerData = (state: RootState) => root(state).footerData
const currentSeason = (state: RootState) => root(state).currentSeason
const isOffline = (state: RootState) => root(state).isOffline
const feedback = (state: RootState) => root(state).feedback
const afterLoginJobs = (state: RootState) => root(state).afterLoginJobs

export const selectors = {
  isInit,
  isLoading,
  menu,
  footerData,
  currentSeason,
  isOffline,
  feedback,
  afterLoginJobs,
}
